import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal";
import DriversHeader from "./_components/DriversHeader";
import DriversTable from "./_components/DriversTable";
import {
  useDeleteDriverMutation,
  useGetDrivers,
} from "../../../../api/fleet-management/drivers/drivers.hooks";
import { filterDrivers, normalizeDrivers } from "./utils/driver.utils";

const Drivers = () => {
  const history = useHistory();
  const { data, isLoading, isError, error } = useGetDrivers();
  const { mutate: deleteDriver, isLoading: isDeleting } = useDeleteDriverMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const drivers = normalizeDrivers(data);

  const filteredDrivers = useMemo(() => {
    return filterDrivers(drivers, searchTerm);
  }, [drivers, searchTerm]);

  const handleEdit = (driverId) => {
    history.push(`/fleet-management/drivers/update/${driverId}`);
  };

  const openDeleteModal = (driver) => {
    setSelectedDriver(driver);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedDriver(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedDriver?._id) return;
    setFeedback({ type: "", message: "" });
    deleteDriver(selectedDriver._id, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Driver deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete driver.",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load drivers. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <DriversHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredDrivers.length}
          onAddDriver={() => history.push("/fleet-management/drivers/add")}
        />
        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredDrivers.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No drivers found</p>
            </div>
          ) : (
            <DriversTable
              drivers={filteredDrivers}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Driver"
        message={`Are you sure you want to delete ${selectedDriver?.name || "this driver"}? This action cannot be undone.`}
        confirmText="Delete Driver"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Drivers;